export type Page =
  | "home"
  | "features"
  | "skill-pack"
  | "releases"
  | "coms"
  | "development-issues"
  | "improvement-candidates"
  | "notifications"
  | "users"
  | "audit";

export interface ReleaseRouteTarget {
  featureKey: string;
  releaseId: string;
}

export const pagePaths: Record<Page, string> = {
  home: "/",
  features: "/features",
  "skill-pack": "/skills",
  releases: "/releases",
  coms: "/coms",
  "development-issues": "/development-issues",
  "improvement-candidates": "/improvement-candidates",
  notifications: "/admin/notifications",
  users: "/users",
  audit: "/audit"
};

const pagesByPath = new Map<string, Page>(
  Object.entries(pagePaths).map(([page, path]) => [path, page as Page])
);

export const skillGuidePaths = new Set([
  "/skills",
  "/skills/setup",
  "/skills/select-feature",
  "/skills/develop",
  "/skills/browser-run",
  "/skills/feature-storage",
  "/skills/modify",
  "/skills/publish",
  "/skills/update-skill-pack",
  "/skills/report-issue",
  "/skills/versioning",
  "/skills/features",
  "/skills/skill-pack",
  "/skills/reference",
  "/skills/troubleshooting"
]);

const featureReleasePathPattern =
  /^\/features\/([a-z0-9][a-z0-9-]{1,62}[a-z0-9])\/releases\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
const developmentIssuePathPattern =
  /^\/development-issues\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
const improvementCandidatePathPattern =
  /^\/improvement-candidates\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;
const notificationDeliveryPathPattern =
  /^\/admin\/notifications\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/;

export function normalizedPathname(pathname: string): string {
  return pathname === "/" ? pathname : pathname.replace(/\/+$/, "");
}

export function releaseRouteTargetFromLocation(): ReleaseRouteTarget | null {
  const pathname = normalizedPathname(window.location.pathname);
  const match = featureReleasePathPattern.exec(pathname);
  return match
    ? { featureKey: match[1]!, releaseId: match[2]! }
    : null;
}

export function releaseRoutePath(target: ReleaseRouteTarget): string {
  return `/features/${target.featureKey}/releases/${target.releaseId}`;
}

export function developmentIssueIdFromLocation(): string | null {
  return developmentIssuePathPattern.exec(
    normalizedPathname(window.location.pathname)
  )?.[1] ?? null;
}

export function improvementCandidateIdFromLocation(): string | null {
  return improvementCandidatePathPattern.exec(
    normalizedPathname(window.location.pathname)
  )?.[1] ?? null;
}

export function notificationDeliveryIdFromLocation(): string | null {
  return notificationDeliveryPathPattern.exec(
    normalizedPathname(window.location.pathname)
  )?.[1] ?? null;
}

export function pageFromLocation(): Page {
  const pathname = normalizedPathname(window.location.pathname);
  if (releaseRouteTargetFromLocation()) return "releases";
  if (developmentIssueIdFromLocation()) return "development-issues";
  if (improvementCandidateIdFromLocation()) return "improvement-candidates";
  if (notificationDeliveryIdFromLocation()) return "notifications";
  if (pathname === "/skills" || pathname.startsWith("/skills/")) {
    return "skill-pack";
  }
  return pagesByPath.get(pathname) ?? "home";
}

export function skillGuidePathFromLocation(): string {
  const pathname = normalizedPathname(window.location.pathname);
  return skillGuidePaths.has(pathname) ? pathname : "/skills";
}
